import { isRtlLocale, resolveBrowserLocale, SupportedLocale, toISOLocale } from '@allurion/utils';
import * as Sentry from '@sentry/react';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { IntlProvider } from 'react-intl';

import { getSupportedTranslationsByLanguage } from './translations';

// `getSupportedTranslationsByLanguage` throws when a locale in SUPPORTED_LOCALES
// has no JSON file behind it. That is a packaging fault rather than anything the
// visitor did, so it is reported and the page degrades to react-intl's default
// messages -- the behaviour before this provider stopped catching, and better
// than failing the render outright.
function loadCatalogue(locale: SupportedLocale): Record<string, string> | undefined {
  try {
    return getSupportedTranslationsByLanguage()[locale];
  } catch (error) {
    Sentry.captureException(error);

    return undefined;
  }
}

export function LocaleProvider({ children }: PropsWithChildren<unknown>) {
  const browserLocale = navigator.language;

  const { intlLocale, messages, isSupported, resolved } = useMemo(() => {
    const { locale, isSupported: supported } = resolveBrowserLocale(browserLocale);

    return {
      resolved: locale,
      // SUPPORTED_LOCALES spells locales with underscores (`pt_BR`), which is not
      // a valid BCP-47 tag -- `Intl` rejects it. Everything handed to react-intl
      // goes through toISOLocale first.
      intlLocale: toISOLocale(locale),
      messages: loadCatalogue(locale),
      isSupported: supported,
    };
  }, [browserLocale]);

  useEffect(() => {
    const documentElement = document.documentElement;

    // The resolved locale, not the browser's: with an unsupported language the
    // page renders in English, and claiming `lang="pl-PL"` over English text
    // misinforms screen readers and translation tooling.
    if (documentElement.getAttribute('lang') !== intlLocale) {
      documentElement.setAttribute('lang', intlLocale);
    }

    // Also the resolved locale. RTL_LOCALES is keyed by language alone, so
    // isRtlLocale('ar-EG') was false and Arabic visitors on a regional tag got
    // a left-to-right layout.
    if (!documentElement.getAttribute('dir')) {
      documentElement.setAttribute('dir', isRtlLocale(resolved) ? 'rtl' : 'ltr');
    }
  }, [intlLocale, resolved]);

  useEffect(() => {
    if (isSupported) {
      return;
    }

    // Not captureException, and not once per render: the visitor gets a working
    // page in English, so an unshipped language is a translation-coverage gap to
    // act on, not a fault. This previously raised two errors per visitor -- one
    // for the throw and one for the resulting empty message catalogue.
    Sentry.captureMessage(`Unsupported browser locale: ${browserLocale}`, 'warning');
  }, [browserLocale, isSupported]);

  return (
    <IntlProvider locale={intlLocale} messages={messages}>
      {children}
    </IntlProvider>
  );
}
